import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import {
  Alert,
  AlertActionLink,
  Breadcrumb,
  BreadcrumbItem,
  Bullseye,
  Button,
  Content,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Flex,
  FlexItem,
  Icon,
  PageBreadcrumb,
  PageSection,
  PageSectionVariants,
  Spinner,
  Tab,
  TabTitleText,
  Tabs,
} from '@patternfly/react-core';
import { ExclamationCircleIcon, LockIcon, SyncAltIcon, TrashIcon } from '@patternfly/react-icons';
import Unauthenticated from '@app/Unauthenticated';
import { useCertificate } from '../../../hooks/useCertificate';
import { useCertificatePlatform } from '../../../hooks/useCertificatePlatform';
import CertificateOverviewTab, { renderCertificateStatusLabel } from './CertificateOverviewTab';
import CertificateX509Tab from './CertificateX509Tab';
import CertificatePemTab from './CertificatePemTab';
import CertificatePrivateKeyTab from './CertificatePrivateKeyTab';
import CertificateDownloadMenu from './CertificateDownloadMenu';
import ZerosslOrderPanel from './ZerosslOrderPanel';
import DeleteZerosslCertificateModal from './DeleteZerosslCertificateModal';

function CertificateDetail() {
  const auth = useAuth();
  const navigate = useNavigate();
  const { certificateId: idParam } = useParams<{ certificateId: string }>();
  const parsedId = Number(idParam);
  const idIsValid = Number.isInteger(parsedId) && parsedId > 0;

  const { certificate, x509, zerosslOrder, loading, error, notFound, refetch } = useCertificate(idIsValid ? parsedId : 0);
  // The platform's `support_private_key` capability flag gates the private
  // key actions; fetched once the certificate row reveals the platform id.
  // The fetch can fail legitimately (staff viewing another tenant's
  // certificate: the certificate Show has a staff bypass, the platform Show
  // does not), so the failure state is forwarded instead of swallowed.
  const {
    platform,
    loading: platformLoading,
    error: platformError,
  } = useCertificatePlatform(certificate?.certificate_platform_id ?? null);

  const [activeTabKey, setActiveTabKey] = useState<string | number>('overview');
  const [isDeleteOrderOpen, setIsDeleteOrderOpen] = useState<boolean>(false);

  // True when the platform can never be known in this view (e.g. staff
  // opening another tenant's certificate: the certificate Show has a staff
  // bypass, the platform Show does not). Key-capability gates then fall open
  // and let the backend's 405 arbitrate. While the platform is merely still
  // loading, capability reads false — hidden until known.
  const platformUnknown = platform === null && platformError !== null;
  const privateKeyCapable = platform !== null ? platform.support_private_key : platformUnknown;
  // A `request`-status row has no PEM/key material yet (the backend answers
  // 409 on both fetches), so the PEM and Private key tabs stay hidden — the
  // order panel above the tabs tells the issuance story instead.
  const isPendingRequest = certificate !== null && certificate.status === 'request';
  const showPemTab = certificate !== null && !isPendingRequest;
  const showPrivateKeyTab = certificate !== null && !isPendingRequest && certificate.has_private_key && privateKeyCapable;

  // A ZeroSSL order that is not issued is the deletable state: pending orders
  // get "Cancel order", failed/upstream-cancelled ones get "Delete". Issued
  // certificates are never deletable (the row holds the only key copy). Same
  // visibility rule as the other title-bar actions: hidden while the page is
  // in an error/not-found state (destructive actions never outlive the data
  // they act on).
  const deletableOrder =
    idIsValid && !error && !notFound && certificate !== null && zerosslOrder !== null && zerosslOrder.state !== 'issued'
      ? zerosslOrder
      : null;

  // If a refetch flips has_private_key to false (stale flag) while the
  // Private key tab is selected, fall back to Overview instead of pointing
  // the Tabs at a tab that no longer exists. An in-flight platform fetch is
  // indeterminate, not "no key" — don't kick the user mid-load when
  // navigating between certificates on different platforms.
  useEffect(() => {
    if (activeTabKey === 'private-key' && !showPrivateKeyTab && !platformLoading) {
      setActiveTabKey('overview');
    }
    if (activeTabKey === 'pem' && certificate !== null && !showPemTab) {
      setActiveTabKey('overview');
    }
  }, [activeTabKey, showPrivateKeyTab, platformLoading, certificate, showPemTab]);

  if (!auth.isAuthenticated) {
    return <Unauthenticated />;
  }

  const titleText = certificate?.cn ?? 'Certificate';
  const breadcrumbLeaf = idIsValid ? (certificate?.cn ?? (loading ? '…' : 'Not found')) : 'Not found';
  // Link back to the owning platform's tab on the list page once the
  // certificate is loaded; before that, fall back to the plain list URL.
  const certificatesListUrl = certificate
    ? `/datacenter/certificates?tab=${certificate.certificate_platform_id}`
    : '/datacenter/certificates';
  // The refresh action stays visible during a refetch (so the button can show
  // its own in-progress spinner); it only requires a loaded entity and no error.
  const showRefresh = idIsValid && !error && !notFound && certificate !== null;
  // Download is meaningless for `request`-status rows (the backend answers
  // 409 until the certificate is issued), so the menu is hidden for them.
  const downloadableCertificate =
    idIsValid && !error && !notFound && certificate !== null && certificate.status !== 'request'
      ? certificate
      : null;

  const notFoundState = (
    <EmptyState titleText="Certificate not found" icon={ExclamationCircleIcon} variant="lg" headingLevel="h2">
      <EmptyStateBody>The requested certificate does not exist.</EmptyStateBody>
      <EmptyStateFooter>
        <EmptyStateActions>
          <Button variant="primary" component={(props) => <Link {...props} to={certificatesListUrl} />}>
            Back to certificates
          </Button>
        </EmptyStateActions>
      </EmptyStateFooter>
    </EmptyState>
  );

  const header = (
    <>
      <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
        <Breadcrumb ouiaId="BasicBreadcrumb">
          <BreadcrumbItem to="/datacenter">Datacenter</BreadcrumbItem>
          <BreadcrumbItem to={certificatesListUrl}>Certificates</BreadcrumbItem>
          <BreadcrumbItem isActive>{breadcrumbLeaf}</BreadcrumbItem>
        </Breadcrumb>
      </PageBreadcrumb>
      <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
        <Flex
          justifyContent={{ default: 'justifyContentSpaceBetween' }}
          alignItems={{ default: 'alignItemsFlexStart' }}
        >
          <FlexItem>
            <Content component="h1">
              <Icon size="lg" style={{ marginRight: '8px' }}>
                <LockIcon />
              </Icon>
              {titleText}
            </Content>
            <Content component="p">
              {certificate !== null && <>{renderCertificateStatusLabel(certificate, zerosslOrder)} </>}
              Certificate details
            </Content>
          </FlexItem>
          {(showRefresh || downloadableCertificate !== null || deletableOrder !== null) && (
            <FlexItem>
              <Flex spaceItems={{ default: 'spaceItemsSm' }}>
                {deletableOrder !== null && (
                  <FlexItem>
                    <Button variant="danger" icon={<TrashIcon />} onClick={() => setIsDeleteOrderOpen(true)}>
                      {deletableOrder.state === 'pending' ? 'Cancel order' : 'Delete'}
                    </Button>
                  </FlexItem>
                )}
                {downloadableCertificate !== null && (
                  <FlexItem>
                    <CertificateDownloadMenu
                      certificate={downloadableCertificate}
                      privateKeyCapable={privateKeyCapable}
                      onCertificateStale={() => void refetch()}
                    />
                  </FlexItem>
                )}
                {showRefresh && (
                  <FlexItem>
                    <Button
                      variant="secondary"
                      icon={<SyncAltIcon />}
                      isLoading={loading}
                      spinnerAriaValueText="Refreshing certificate"
                      onClick={() => {
                        if (!loading) {
                          void refetch();
                        }
                      }}
                    >
                      Refresh
                    </Button>
                  </FlexItem>
                )}
              </Flex>
            </FlexItem>
          )}
        </Flex>
      </PageSection>
    </>
  );

  const renderContent = () => {
    if (!idIsValid || notFound) {
      return notFoundState;
    }

    // Full-page spinner only on the initial load; during a refetch the existing
    // content stays visible while the Refresh button shows its own spinner.
    if (loading && certificate === null) {
      return (
        <Bullseye>
          <Spinner size="xl" aria-label="Loading certificate" />
        </Bullseye>
      );
    }

    if (error) {
      return (
        <Alert
          variant="danger"
          isInline
          title="Failed to load certificate"
          actionLinks={<AlertActionLink onClick={() => void refetch()}>Retry</AlertActionLink>}
        >
          {error}
        </Alert>
      );
    }

    if (certificate === null) {
      return notFoundState;
    }

    return (
      <>
        {/* The live order view for a not-yet-issued ZeroSSL row: validation
            instructions + 10s polling. Keyed per certificate so its internal
            pair state can never leak across navigations; once a poll settles
            the order (issued/failed/cancelled) the Show refetch replaces this
            panel with the final state (issued rows drop it entirely). */}
        {zerosslOrder !== null && certificate.status === 'request' && (
          <div className="pf-v6-u-mb-md">
            <ZerosslOrderPanel
              key={certificate.id}
              certificate={certificate}
              order={zerosslOrder}
              onPairChange={(_cert, order) => {
                if (order.state !== 'pending') {
                  void refetch();
                }
              }}
              // Row deleted from another session: the refetch 404s and flips
              // the page to its not-found state (unmounting this panel).
              onOrderMissing={() => void refetch()}
            />
          </div>
        )}
        <Tabs
          activeKey={activeTabKey}
          onSelect={(_event, eventKey) => setActiveTabKey(eventKey)}
          isBox
          mountOnEnter
          aria-label="Certificate detail sections"
        >
          <Tab eventKey="overview" title={<TabTitleText>Overview</TabTitleText>}>
            <CertificateOverviewTab certificate={certificate} zerosslOrder={zerosslOrder} />
          </Tab>
          <Tab eventKey="x509" title={<TabTitleText>X.509 details</TabTitleText>}>
            <CertificateX509Tab x509={x509} />
          </Tab>
          {/* The PEM and Private key tabs fetch their own data (the Show
              response intentionally omits the PEM) — kept lazy by this Tabs'
              mountOnEnter, and keyed on the certificate id so navigating to
              another certificate drops their state (incl. any loaded key).
              Both are hidden on `request`-status rows (409 until issued). */}
          {showPemTab && (
            <Tab eventKey="pem" title={<TabTitleText>PEM</TabTitleText>}>
              <CertificatePemTab key={certificate.id} certificateId={certificate.id} />
            </Tab>
          )}
          {showPrivateKeyTab && (
            <Tab eventKey="private-key" title={<TabTitleText>Private key</TabTitleText>}>
              <CertificatePrivateKeyTab
                key={certificate.id}
                certificateId={certificate.id}
                onCertificateStale={() => void refetch()}
              />
            </Tab>
          )}
        </Tabs>
      </>
    );
  };

  return (
    <>
      {header}
      <PageSection hasBodyWrapper={false}>{renderContent()}</PageSection>

      {isDeleteOrderOpen && certificate !== null && deletableOrder !== null && (
        <DeleteZerosslCertificateModal
          certificate={certificate}
          orderState={deletableOrder.state}
          onClose={() => setIsDeleteOrderOpen(false)}
          onDeleted={() => navigate(certificatesListUrl)}
        />
      )}
    </>
  );
}

export default CertificateDetail;
